import React from 'react';
import { useTranslation } from '../../../../i18n';
import WorkPreferenceBarChart from '../../../../components/WorkPreferenceBarChart/WorkPreferenceBarChart';
import { CultureFitCategory } from '../../../../typings';
import styles from './styles.module.css';
import SingleBarChart from '../../../../components/SingleBarChart';
import {
    getHighestCat,
    getWorkPreferencesFormattedData,
} from '../../../CultureFitWorkPreferences/utils';
import './styles.css';
import globalStyles from '../../../../styles/global.module.css';
import { useReportData } from '../../../../GlobalStateContext';

interface WorkPreferencesProps {
    companyName: string;
    cultureMatchScore?: number;
    hasBaseline: boolean;
    workPreferenceScores: CultureFitCategory[];
    title: string;
    userRole: 'recruiter' | 'candidate';
    isMatchScoreExpected: boolean;
}

const SummaryWorkPreferences = (props: WorkPreferencesProps) => {
    const {
        title,
        companyName,
        cultureMatchScore,
        hasBaseline,
        workPreferenceScores,
        isMatchScoreExpected,
        userRole,
    } = props;
    const { t } = useTranslation();
    const { state } = useReportData();
    const { baselineRange } = state.reportData;

    const data = getWorkPreferencesFormattedData(
        companyName,
        workPreferenceScores,
        t,
        hasBaseline,
    );

    return (
        <div className={styles.workPreferencesContainer}>
            <h3 className={styles.workPreferencesContainerTitle}>{title}</h3>
            <div className={styles.workPreferencesContainerChartWrap}>
                <div className={styles.workPreferencesContainerDescWrap}>
                    <div className={styles.workPreferencesPreferredEnv}>
                        <span
                            className={styles.workPreferencesPreferredEnvText}>
                            {t(
                                'CULTURAL_FIT.WORK_PREFERENCES.YOUR_ENVIRONMENT_DESC',
                            )}
                        </span>
                        <span
                            className={styles.workPreferencesPreferredEnvLabel}>
                            {t(
                                `CULTURAL_FIT.WORK_PREFERENCES.CATEGORIES.${getHighestCat(
                                    data[0],
                                    baselineRange,
                                ).toUpperCase()}`,
                            )}
                        </span>
                    </div>
                    {data[1] && (
                        <div
                            className={`${styles.workPreferencesPreferredEnv} ${styles.workPreferencesPreferredEnvCompany}`}>
                            <span
                                className={
                                    styles.workPreferencesPreferredEnvText
                                }>
                                {t(
                                    'CULTURAL_FIT.WORK_PREFERENCES.COMPANY_ENVIRONMENT_DESC',
                                    { company_name: companyName },
                                )}
                            </span>
                            <span
                                className={
                                    styles.workPreferencesPreferredEnvLabel
                                }>
                                {t(
                                    `CULTURAL_FIT.WORK_PREFERENCES.CATEGORIES.${getHighestCat(
                                        data[1],
                                        baselineRange,
                                    ).toUpperCase()}`,
                                )}
                            </span>
                        </div>
                    )}
                </div>

                <div
                    id="summary-work-preferences-bar-chart"
                    style={{ width: '100%' }}>
                    <WorkPreferenceBarChart data={data} />
                </div>
            </div>
            {isMatchScoreExpected && !!cultureMatchScore && (
                <SingleBarChart
                    title={t('SUMMARY_PAGE.WORK_PREFERENCES.MATCH_SCORE_TITLE')}
                    titleTag="h3"
                    value={Math.trunc(cultureMatchScore)}
                    max={100}
                    minLabel={t(
                        'CULTURAL_FIT.WORK_PREFERENCES.BAR_CHART.MATCH_SCORE_BAR_MIN_LABEL',
                    )}
                    maxLabel={t(
                        'CULTURAL_FIT.WORK_PREFERENCES.BAR_CHART.MATCH_SCORE_BAR_MAX_LABEL',
                    )}
                    hasScoreValue={userRole === 'recruiter'}
                    hasScoreLabel={true}
                    hasUserIcon={false}
                    hasScoreCheckpoints={true}
                    resultLabels={{
                        low: t(
                            'CULTURAL_FIT.WORK_PREFERENCES.BAR_CHART.RESULT_LABEL.LOW',
                        ),
                        mid: t(
                            'CULTURAL_FIT.WORK_PREFERENCES.BAR_CHART.RESULT_LABEL.MID',
                        ),
                        high: t(
                            'CULTURAL_FIT.WORK_PREFERENCES.BAR_CHART.RESULT_LABEL.HIGH',
                        ),
                    }}
                />
            )}
            {isMatchScoreExpected && !cultureMatchScore && (
                <>
                    <h4
                        className={`${globalStyles.textSummaryTitle} ${globalStyles.textNoAboveSpace}`}>
                        {t('SUMMARY_PAGE.WORK_PREFERENCES.MATCH_SCORE_TITLE')}
                    </h4>
                    <div className={`${globalStyles.textNoScoreDisplay}`}>
                        <svg
                            width="24"
                            height="25"
                            viewBox="0 0 27 28"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <circle
                                cx="13.5"
                                cy="14.376"
                                r="13.5"
                                fill="#F2D04B"
                            />
                            <path
                                d="M12.32 7.87598H15.28L14.78 17.236H12.8L12.32 7.87598ZM13.8 22.016C13.3333 22.016 12.9467 21.8693 12.64 21.576C12.3333 21.2693 12.18 20.896 12.18 20.456C12.18 20.016 12.3333 19.6493 12.64 19.356C12.9467 19.0626 13.3333 18.916 13.8 18.916C14.2533 18.916 14.6267 19.0626 14.92 19.356C15.2267 19.6493 15.38 20.016 15.38 20.456C15.38 20.896 15.2267 21.2693 14.92 21.576C14.6267 21.8693 14.2533 22.016 13.8 22.016Z"
                                fill="white"
                            />
                        </svg>
                        <span
                            className={`${globalStyles.text} ${globalStyles.textPaddingTop} ${globalStyles.textNoBelowSpace} ${globalStyles.textNoAboveSpace}`}>
                            {t(`NO_SCORE_AVAILABLE`)}
                        </span>
                    </div>
                </>
            )}
        </div>
    );
};

export default SummaryWorkPreferences;
